import { portalApiAxios } from 'store/api/portalAPI';

interface BlacklistResponse {
    blacklisted: boolean;
}

/**
 * The endpoint returns a hardcoded answer and does no I/O, so anything this slow is a fault.
 * Set per request rather than on the shared `portalApiAxios` instance, which also carries
 * long-running uploads.
 */
const REQUEST_TIMEOUT_MS = 5_000;

/**
 * - `allowed`     — the domain is not blocked, the reset may proceed.
 * - `blocked`     — the domain is blocked and must use Microsoft SSO instead.
 * - `unavailable` — the check could not be completed, so we do not know. Treated as blocking.
 */
export type PasswordResetAvailability = 'allowed' | 'blocked' | 'unavailable';

/**
 * Extracts the domain part of an email address, or null when there is nothing usable after the
 * '@'. Only the domain is ever sent to the backend — never the full address.
 */
export const emailDomainOf = (email: string): string | null => {
    const separator = email.lastIndexOf('@');
    if (separator < 0) {
        return null;
    }
    const domain = email.slice(separator + 1).trim();
    return domain.length > 0 ? domain : null;
};

/**
 * Asks the backend whether self-service password reset is blocked for this email's domain.
 *
 * Fails closed: if the check cannot complete (network error, rate limit, backend down) the result
 * is `unavailable` and the caller must not send a reset code. Callers should say the check failed
 * rather than claim the domain is blocked — the two are not the same thing to the user.
 */
export const checkPasswordResetAvailability = async (email: string): Promise<PasswordResetAvailability> => {
    const domain = emailDomainOf(email);
    if (!domain) {
        return 'unavailable';
    }
    try {
        const { data } = await portalApiAxios.get<BlacklistResponse>('/v1/password-reset/blacklist', {
            params: { domain },
            // Scoped to this request: without it a hanging call would leave the form submitting
            // forever with no message. A timeout rejects, which fails closed like any other error.
            timeout: REQUEST_TIMEOUT_MS,
        });
        // `BlacklistResponse` is only a compile-time annotation, so an unexpected 2xx body (an
        // empty object, a proxy's HTML error page) would otherwise read as 'allowed' and defeat
        // the fail-closed guarantee.
        if (typeof data?.blacklisted !== 'boolean') {
            console.error('Password-reset blacklist check returned an unexpected body; blocking the reset');
            return 'unavailable';
        }
        return data.blacklisted ? 'blocked' : 'allowed';
    } catch (err) {
        console.error('Password-reset blacklist check failed; blocking the reset', err);
        return 'unavailable';
    }
};
